import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { Wrapper, Flex, Box, Text } from 'roo-ui/components';
import { PropertySummaryMain, PropertySummaryAside } from './primitives';
import ImageGallery from 'components/ImageGallery';
import CheckInInfo from 'pages/Property/CheckInInfo';
import AboutThisProperty from 'pages/Property/AboutThisProperty';
import PropertyMapStatic from 'pages/Property/PropertyMapStatic';
import PropertyHeader from 'pages/Property/PropertyHeader';
import PropertyFacilities from 'pages/Property/PropertyFacilities';
import formatPropertyAddress from 'components/formatters/FormatPropertyAddress';
import { useDataLayer } from 'hooks/useDataLayer';
import Renovations from 'pages/Property/Renovations';

const propertyAddress = (address) =>
  formatPropertyAddress({
    streetAddressLines: address.streetAddress,
    suburb: address.suburb,
    state: address.state,
    country: address.country,
  });

const PropertySummary = ({ property, tripAdvisorRating, ...rest }) => {
  const hasPropertyFacilities = property.propertyFacilities && property.propertyFacilities.length > 0;
  const { emitInteractionEvent } = useDataLayer();
  const handleGalleryOpened = useCallback(() => {
    emitInteractionEvent({ type: 'Property Summary Gallery', value: 'Gallery Opened' });
  }, [emitInteractionEvent]);

  const handleGalleryImageChanged = useCallback(
    (id) => {
      emitInteractionEvent({ type: 'Property Summary Gallery', value: `Gallery Scrolled to ${id}` });
    },
    [emitInteractionEvent],
  );

  return (
    <Wrapper borderBottom={1} borderColor="greys.alto" {...rest}>
      <PropertyHeader property={property} tripAdvisorRating={tripAdvisorRating} display={['none', 'block']} bg="white" pt={10} pb={6} />

      <Flex flex="1 1 auto" pb={6} flexDirection={['column', 'column', 'row']}>
        <PropertySummaryMain>
          <ImageGallery
            images={property.images}
            onClick={handleGalleryOpened}
            onImageChanged={handleGalleryImageChanged}
            imageWidth="100%"
            imageHeight={['220px', '480px']}
            mb={[5, 0]}
            imageSize="large"
          />

          <PropertyHeader property={property} tripAdvisorRating={tripAdvisorRating} display={['block', 'none']} />
        </PropertySummaryMain>

        <PropertySummaryAside>
          <Box>
            {hasPropertyFacilities && (
              <PropertyFacilities
                facilities={property.propertyFacilities}
                propertyName={property.name}
                summary
                mb="6"
                pb="6"
                borderBottom="1"
                borderColor={['greys.alto', 'transparent', 'greys.alto']}
                interactionEventValue="Top Link Selected"
              />
            )}
            <CheckInInfo property={property} mb={4} />
            <AboutThisProperty property={property} mb={[6, 0]} />
          </Box>
          <Flex
            flexDirection="column"
            alignItems={['center', 'center', 'flex-end']}
            mt={[0, 0, 'auto']}
            data-testid="property-map-static-desktop-container"
          >
            <PropertyMapStatic
              locationName={property.name}
              latitude={property.latitude}
              longitude={property.longitude}
              width={[640, 640, 400]}
              height={[200, 400, 220]}
            />
            <Text mt={[0, 4]} display={['block', 'block', 'none']} fontSize="base" color="greys.charcoal" textAlign="center">
              {propertyAddress(property.address)}
            </Text>
          </Flex>
        </PropertySummaryAside>
      </Flex>
      {property.renovations && property.renovations !== 'undefined' && (
        <Box mb={6}>
          <Renovations renovations={property.renovations} />
        </Box>
      )}
    </Wrapper>
  );
};

PropertySummary.propTypes = {
  property: PropTypes.object.isRequired,
  tripAdvisorRating: PropTypes.object,
};

PropertySummary.defaultProps = {
  tripAdvisorRating: null,
};

export default PropertySummary;
